import { StyleType, CssColor, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import { createStylesParam, createStylesParams, StyleParamType, wixColorParam, wixFontParam } from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { Alignment } from '../../utils/alignment';

export enum FormFieldBorderStyle {
  None = 0,
  All = 1,
  Bottom = 2,
}

export enum ButtonStyle {
  Rectangle = 0,
  RectangleRoundedCorners = 1,
  RectangleFilled = 2,
  RectangleRoundedCornersFilled = 3,
}

type PageCustomizationStylesParams = {
  // Layout
  pageHorizontalPadding: StyleParamType.Number;
  pageVerticalPadding: StyleParamType.Number;
  pageColumnSpacing: StyleParamType.Number;
  planFormRowSpacing: StyleParamType.Number;
  planFormPadding: StyleParamType.Number;
  planCardImageHeight: StyleParamType.Number;
  planCardAlignment: StyleParamType.Number;

  // Display
  showPageTitle: StyleParamType.Boolean;
  showPlanCard: StyleParamType.Boolean;
  showPlanCardImage: StyleParamType.Boolean;
  showPlanCardName: StyleParamType.Boolean;
  showPlanCardDescription: StyleParamType.Boolean;
  showPlanCardPerks: StyleParamType.Boolean;

  // Background & Border
  pageBackgroundColor: StyleParamType.Color;
  planFormBackgroundColor: StyleParamType.Color;
  planFormBorderColor: StyleParamType.Color;
  planFormBorderRadius: StyleParamType.Number;
  planFormBorderTopLeftRadius: StyleParamType.Number;
  planFormBorderTopRightRadius: StyleParamType.Number;
  planFormBorderBottomRightRadius: StyleParamType.Number;
  planFormBorderBottomLeftRadius: StyleParamType.Number;
  planFormBorderWidth: StyleParamType.Number;
  planFormEnableShadow: StyleParamType.Boolean;
  planFormShadowAngle: StyleParamType.Number;
  planFormShadowDistance: StyleParamType.Number;
  planFormShadowBlur: StyleParamType.Number;
  planFormShadowColor: StyleParamType.Color;
  planCardBackgroundColor: StyleParamType.Color;
  planCardBorderColor: StyleParamType.Color;
  planCardBorderWidth: StyleParamType.Number;
  planCardBorderRadius: StyleParamType.Number;
  planCardBorderTopLeftRadius: StyleParamType.Number;
  planCardBorderTopRightRadius: StyleParamType.Number;
  planCardBorderBottomRightRadius: StyleParamType.Number;
  planCardBorderBottomLeftRadius: StyleParamType.Number;
  planCardEnableShadow: StyleParamType.Boolean;
  planCardShadowAngle: StyleParamType.Number;
  planCardShadowDistance: StyleParamType.Number;
  planCardShadowBlur: StyleParamType.Number;
  planCardShadowColor: StyleParamType.Color;

  // Typography
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  planFormFieldLabelColor: StyleParamType.Color;
  planFormFieldLabelFont: StyleParamType.Font;
  planFormFieldInputColor: StyleParamType.Color;
  planFormFieldInputFont: StyleParamType.Font;
  planFormFieldDescriptionColor: StyleParamType.Color;
  planFormFieldDescriptionFont: StyleParamType.Font;
  planFormFieldPlaceholderColor: StyleParamType.Color;
  planFormFieldLinkColor: StyleParamType.Color;
  planFormHeader1Font: StyleParamType.Font;
  planFormHeader1Color: StyleParamType.Color;
  planFormHeader2Font: StyleParamType.Font;
  planFormHeader2Color: StyleParamType.Color;
  planFormParagraphFont: StyleParamType.Font;
  planFormParagraphColor: StyleParamType.Color;
  planFormDisclaimerFont: StyleParamType.Font;
  planFormDisclaimerColor: StyleParamType.Color;
  planCardNameFont: StyleParamType.Font;
  planCardNameColor: StyleParamType.Color;
  planCardPriceFont: StyleParamType.Font;
  planCardPriceColor: StyleParamType.Color;
  planCardPaymentFrequencyFont: StyleParamType.Font;
  planCardPaymentFrequencyColor: StyleParamType.Color;
  planCardSetupFeeFont: StyleParamType.Font;
  planCardSetupFeeColor: StyleParamType.Color;
  planCardDescriptionFont: StyleParamType.Font;
  planCardDescriptionColor: StyleParamType.Color;
  planCardDurationFont: StyleParamType.Font;
  planCardDurationColor: StyleParamType.Color;

  // Form fields
  planFormFieldBorderStyle: StyleParamType.Number;
  planFormFieldBackgroundColor: StyleParamType.Color;
  planFormFieldBackgroundColorHover: StyleParamType.Color;
  planFormFieldBorderRadius: StyleParamType.Number;
  planFormFieldBorderColor: StyleParamType.Color;
  planFormFieldBorderColorHover: StyleParamType.Color;
  planFormFieldBorderWidth: StyleParamType.Number;
  planFormFieldCheckedColor: StyleParamType.Color;
  planFormFieldErrorColor: StyleParamType.Color;

  // Buttons
  planFormCtaButtonTextColor: StyleParamType.Color;
  planFormCtaButtonTextColorHover: StyleParamType.Color;
  planFormCtaButtonTextFont: StyleParamType.Font;
  planFormCtaButtonBackgroundColor: StyleParamType.Color;
  planFormCtaButtonBackgroundColorHover: StyleParamType.Color;
  planFormCtaButtonBorderColor: StyleParamType.Color;
  planFormCtaButtonBorderColorHover: StyleParamType.Color;
  planFormCtaButtonBorderRadius: StyleParamType.Number;
  planFormCtaButtonBorderWidth: StyleParamType.Number;
  planFormButtonsTextColor: StyleParamType.Color;
  planFormButtonsTextColorHover: StyleParamType.Color;
  planFormButtonsTextFont: StyleParamType.Font;
  planFormButtonsBackgroundColor: StyleParamType.Color;
  planFormButtonsBackgroundColorHover: StyleParamType.Color;
  planFormButtonsBorderColor: StyleParamType.Color;
  planFormButtonsBorderColorHover: StyleParamType.Color;
  planFormButtonsBorderRadius: StyleParamType.Number;
  planFormButtonsBorderWidth: StyleParamType.Number;
};

const componentStyles = createStylesParams<PageCustomizationStylesParams>({
  // Layout
  pageHorizontalPadding: createStylesParam('page-horizontal-padding', {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.padding,
      ai: {
        description: 'Side paddings around the entire plan customization widget in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  }),
  pageVerticalPadding: createStylesParam('page-vertical-padding', {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.padding,
      ai: {
        description: 'Top and bottom paddings around the entire plan customization widget in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  }),
  pageColumnSpacing: createStylesParam('page-column-spacing', {
    type: StyleParamType.Number,
    getDefaultValue: () => 40,
    metadata: {
      type: StyleType.gap,
      ai: {
        description:
          'Horizontal spacing between the plan customization form and the plan preview card when displayed in a two-column layout in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 40,
      },
    },
  }),
  planFormRowSpacing: createStylesParam('plan-form-row-spacing', {
    type: StyleParamType.Number,
    getDefaultValue: () => 24,
    metadata: {
      type: StyleType.gap,
      ai: {
        description: 'Vertical spacing between the rows of the plan customization form in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 24,
      },
    },
  }),
  planFormPadding: createStylesParam('plan-form-padding', {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.padding,
      ai: {
        description: 'Paddings around the entire plan customization form container in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  }),
  planCardImageHeight: createStylesParam('plan-card-image-height', {
    type: StyleParamType.Number,
    getDefaultValue: () => 160,
    metadata: {
      type: StyleType.height,
      ai: {
        description: 'Height of the plan cover image displayed at the top of the plan preview card in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 160,
      },
    },
  }),
  planCardAlignment: createStylesParam('plan-card-alignment', {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.CENTER,
    metadata: {
      type: StyleType.alignItems,
      ai: {
        description:
          'Text alignment of all content within the plan preview card (plan title, price, billing frequency, description, plan duration). You can use this to change the text alignment to LEFT, CENTER, or RIGHT.',
      },
      defaultValue: {
        type: 'static',
        // TODO: Support this in component style implementation
        value: 'center',
      },
    },
  }),

  // Display
  showPageTitle: createStylesParam('show-page-title', {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      ai: {
        description: 'Whether to show the page title. You can use this to show or hide the page title.',
      },
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  }),
  showPlanCard: createStylesParam('show-plan-card', {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ isMobile }) => (isMobile ? false : true),
    metadata: {
      type: StyleType.booleanValue,
      ai: {
        description:
          'Whether to show the plan preview card that displays alongside the customization form. You can use this to show or hide the plan preview card.',
      },
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  }),
  showPlanCardImage: createStylesParam('show-plan-card-image', {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      ai: {
        description:
          'Whether to show the plan cover image displayed at the top of the plan preview card. You can use this to show or hide the plan cover image.',
      },
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  }),
  showPlanCardName: createStylesParam('show-plan-card-name', {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      ai: {
        description:
          'Whether to show the plan name displayed at the top of the plan preview card. You can use this to show or hide the plan name.',
      },
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  }),
  showPlanCardDescription: createStylesParam('show-plan-card-description', {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      ai: {
        description:
          'Whether to show the plan description displayed below the plan name and price in the plan preview card. You can use this to show or hide the plan description.',
      },
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  }),
  showPlanCardPerks: createStylesParam('show-plan-card-perks', {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      ai: {
        description:
          'Whether to show the plan benefits list on the plan preview card. You can use this to show or hide the plan benefits list.',
      },
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  }),

  // Background & Border
  pageBackgroundColor: createStylesParam('page-background-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary', 0),
    metadata: {
      type: StyleType.backgroundColor,
      ai: {
        description:
          'Background color of the entire plan customization widget. You can use this to change the background color.',
      },
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  }),
  planFormBackgroundColor: createStylesParam('plan-form-background-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary', 0),
    metadata: {
      type: StyleType.backgroundColor,
      ai: {
        description:
          'Background color of the plan customization form container. You can use this to change the background color.',
      },
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  }),
  planFormBorderColor: createStylesParam('plan-form-border-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.2),
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description:
          'Border color of the plan customization form container. You can use this to change the border color.',
      },
      defaultValue: wixColor(CssColor.WstSystemLine1Color),
    },
  }),
  planFormBorderRadius: createStylesParam('plan-form-border-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  }),
  planFormBorderTopLeftRadius: createStylesParam('plan-form-border-top-left-radius', {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const fallbackValue = params.getStyleParamValue({
        type: StyleParamType.Number,
        key: 'plan-form-border-radius',
      });
      return fallbackValue ?? 0;
    },
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planFormBorderTopRightRadius: createStylesParam('plan-form-border-top-right-radius', {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const fallbackValue = params.getStyleParamValue({
        type: StyleParamType.Number,
        key: 'plan-form-border-radius',
      });
      return fallbackValue ?? 0;
    },
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planFormBorderBottomRightRadius: createStylesParam('plan-form-border-bottom-right-radius', {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const fallbackValue = params.getStyleParamValue({
        type: StyleParamType.Number,
        key: 'plan-form-border-radius',
      });
      return fallbackValue ?? 0;
    },
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planFormBorderBottomLeftRadius: createStylesParam('plan-form-border-bottom-left-radius', {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const fallbackValue = params.getStyleParamValue({
        type: StyleParamType.Number,
        key: 'plan-form-border-radius',
      });
      return fallbackValue ?? 0;
    },
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planFormBorderWidth: createStylesParam('plan-form-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      ai: {
        description: 'Border width of the plan customization form container in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  // TODO: Support shadow in editor 3
  planFormEnableShadow: createStylesParam('plan-form-shadow', {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  }),
  planFormShadowAngle: createStylesParam('plan-form-shadow-angle', {
    type: StyleParamType.Number,
    getDefaultValue: () => 315,
  }),
  planFormShadowDistance: createStylesParam('plan-form-shadow-distance', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  }),
  planFormShadowBlur: createStylesParam('plan-form-shadow-blur', {
    type: StyleParamType.Number,
    getDefaultValue: () => 25,
  }),
  planFormShadowColor: createStylesParam('plan-form-shadow-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.2),
  }),
  planCardBackgroundColor: createStylesParam('plan-card-background-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary', 0),
    metadata: {
      type: StyleType.backgroundColor,
      ai: {
        description: 'Background color of the plan preview card. You can use this to change the background color.',
      },
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  }),
  planCardBorderColor: createStylesParam('plan-card-border-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.2),
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description: 'Border color of the plan preview card. You can use this to change the border color.',
      },
      defaultValue: wixColor(CssColor.WstSystemLine1Color),
    },
  }),
  planCardBorderWidth: createStylesParam('plan-card-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      ai: {
        description: 'Border width of the plan preview card in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  }),
  planCardBorderRadius: createStylesParam('plan-card-border-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  }),
  planCardBorderTopLeftRadius: createStylesParam('plan-card-border-top-left-radius', {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const fallbackValue = params.getStyleParamValue({
        type: StyleParamType.Number,
        key: 'plan-card-border-radius',
      });
      return fallbackValue ?? 0;
    },
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planCardBorderTopRightRadius: createStylesParam('plan-card-border-top-right-radius', {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const fallbackValue = params.getStyleParamValue({
        type: StyleParamType.Number,
        key: 'plan-card-border-radius',
      });
      return fallbackValue ?? 0;
    },
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planCardBorderBottomRightRadius: createStylesParam('plan-card-border-bottom-right-radius', {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const fallbackValue = params.getStyleParamValue({
        type: StyleParamType.Number,
        key: 'plan-card-border-radius',
      });
      return fallbackValue ?? 0;
    },
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planCardBorderBottomLeftRadius: createStylesParam('plan-card-border-bottom-left-radius', {
    type: StyleParamType.Number,
    getDefaultValue: (params) => {
      const fallbackValue = params.getStyleParamValue({
        type: StyleParamType.Number,
        key: 'plan-card-border-radius',
      });
      return fallbackValue ?? 0;
    },
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  // TODO: Support shadow in editor 3
  planCardEnableShadow: createStylesParam('plan-card-apply-shadow', {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  }),
  planCardShadowAngle: createStylesParam('plan-card-shadow-angle', {
    type: StyleParamType.Number,
    getDefaultValue: () => 315,
  }),
  planCardShadowDistance: createStylesParam('plan-card-shadow-distance', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  }),
  planCardShadowBlur: createStylesParam('plan-card-shadow-blur', {
    type: StyleParamType.Number,
    getDefaultValue: () => 25,
  }),
  planCardShadowColor: createStylesParam('plan-card-shadow-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.2),
  }),

  // Typography
  pageTitleFont: createStylesParam('page-title-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M', { htmlTag: 'h1' }),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     // TODO: Check if this works without htmlTag
    //     preset: 'Heading-M',
    //   },
    // },
  }),
  pageTitleColor: createStylesParam('page-title-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-title'),
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Text color of the plan customization widget title. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstHeading1Color),
    },
  }),
  planFormFieldLabelColor: createStylesParam('plan-form-field-label-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form field labels. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planFormFieldLabelFont: createStylesParam('plan-form-field-label-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-M',
    //   },
    // },
  }),
  planFormFieldInputColor: createStylesParam('plan-form-field-input-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form field inputs. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planFormFieldInputFont: createStylesParam('plan-form-field-input-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-M',
    //   },
    // },
  }),
  planFormFieldDescriptionColor: createStylesParam('plan-form-field-description-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form field descriptions. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planFormFieldDescriptionFont: createStylesParam('plan-form-field-description-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-M',
    //   },
    // },
  }),
  planFormFieldPlaceholderColor: createStylesParam('plan-form-field-placeholder-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-secondary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form field placeholders. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  }),
  planFormFieldLinkColor: createStylesParam('plan-form-field-link-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-action'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form field links. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  }),
  planFormHeader1Font: createStylesParam('plan-form-header-1-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-L'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Heading-L',
    //   },
    // },
  }),
  planFormHeader1Color: createStylesParam('plan-form-header-1-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form header 1 text. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planFormHeader2Font: createStylesParam('plan-form-header-2-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Heading-M',
    //   },
    // },
  }),
  planFormHeader2Color: createStylesParam('plan-form-header-2-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form header 2 text. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planFormParagraphFont: createStylesParam('plan-form-paragraph-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-M',
    //   },
    // },
  }),
  planFormParagraphColor: createStylesParam('plan-form-paragraph-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form paragraph text. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planFormDisclaimerFont: createStylesParam('plan-form-disclaimer-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-S',
    //   },
    // },
  }),
  planFormDisclaimerColor: createStylesParam('plan-form-disclaimer-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-secondary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan customization form disclaimer text. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  }),
  planCardNameFont: createStylesParam('plan-card-name-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-L'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-L',
    //   },
    // },
  }),
  planCardNameColor: createStylesParam('plan-card-name-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan name displayed at the top of the plan preview card. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planCardPriceFont: createStylesParam('plan-card-price-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 48, lineHeight: 56 }),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Page-title',
    //   },
    // },
  }),
  planCardPriceColor: createStylesParam('plan-card-price-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan price displayed at the top of the plan preview card. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planCardPaymentFrequencyFont: createStylesParam('plan-card-payment-frequency-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-S',
    //   },
    // },
  }),
  planCardPaymentFrequencyColor: createStylesParam('plan-card-payment-frequency-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan billing frequency displayed at the top of the plan preview card. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planCardSetupFeeFont: createStylesParam('plan-card-setup-fee-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-S',
    //   },
    // },
  }),
  planCardSetupFeeColor: createStylesParam('plan-card-setup-fee-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan setup fee displayed at the top of the plan preview card. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planCardDescriptionFont: createStylesParam('plan-card-description-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-S',
    //   },
    // },
  }),
  planCardDescriptionColor: createStylesParam('plan-card-description-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan description displayed at the top of the plan preview card. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph1Color),
    },
  }),
  planCardDurationFont: createStylesParam('plan-card-duration-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S', { size: 12 }),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-S',
    //   },
    // },
  }),
  planCardDurationColor: createStylesParam('plan-card-duration-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-text-secondary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color of the plan duration displayed at the top of the plan preview card. You can use this to change the text color.',
      },
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  }),

  // Form fields
  planFormFieldBorderStyle: createStylesParam('plan-form-field-border-style', {
    type: StyleParamType.Number,
    getDefaultValue: () => FormFieldBorderStyle.All,
    // TODO: Check how to support this
    // metadata: {
    //   type: StyleType.borderStyle,
    //   ai: {
    //     description:
    //       'Border display style for form input fields: None (no borders), All (borders on all sides), or Bottom (bottom border only)',
    //   },
    //   defaultValue: {
    //     type: 'static',
    //     value: ''
    //   },
    // },
  }),
  planFormFieldBackgroundColor: createStylesParam('plan-form-field-background-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Background color of the plan customization form field input elements. You can use this to change the background color.',
      },
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  }),
  planFormFieldBackgroundColorHover: createStylesParam('plan-form-field-background-color-hover', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-fill-background-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Background color of the plan customization form field input elements when hovered. You can use this to change the background color.',
      },
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  }),
  planFormFieldBorderRadius: createStylesParam('plan-form-field-border-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      ai: {
        description:
          'Border radius of the plan customization form field input elements in pixels. You can use this to change the border radius.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planFormFieldBorderColor: createStylesParam('plan-form-field-border-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.2),
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description:
          'Border color of the plan customization form field input elements. You can use this to change the border color.',
      },
      defaultValue: wixColor(CssColor.WstSystemLine1Color),
    },
  }),
  planFormFieldBorderColorHover: createStylesParam('plan-form-field-border-color-hover', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-line', 0.4),
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description:
          'Border color of the plan customization form field input elements when hovered. You can use this to change the border color.',
      },
      defaultValue: wixColor(CssColor.WstSystemLine1Color),
    },
  }),
  planFormFieldBorderWidth: createStylesParam('plan-form-field-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      ai: {
        description:
          'Border width of the plan customization form field input elements in pixels. You can use this to change the border width.',
      },
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  }),
  planFormFieldCheckedColor: createStylesParam('plan-form-field-checked-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-action'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Color of selected/checked state for checkboxes and radio buttons in plan customization form fields. You can use this to change the color.',
      },
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  }),
  planFormFieldErrorColor: createStylesParam('plan-form-field-error-color', {
    type: StyleParamType.Color,
    getDefaultValue: () => ({
      name: null,
      opacity: 1,
      value: '#DF3131',
    }),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Color for error messages and error state indicators displayed on invalid plan customization form fields. You can use this to change the color.',
      },
      defaultValue: wixColor(CssColor.WstSystemErrorColor),
    },
  }),

  // Buttons
  planFormCtaButtonTextColor: createStylesParam('plan-form-cta-button-text-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color for the primary call-to-action button (e.g., Submit, Continue) on the plan customization form. You can use this to change the text color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-text-primary',
      },
    },
  }),
  planFormCtaButtonTextColorHover: createStylesParam('plan-form-cta-button-text-color-hover', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-text-primary-hover'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color for the primary call-to-action button (e.g., Submit, Continue) on the plan customization form when hovered. You can use this to change the text color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-text-primary-hover',
      },
    },
  }),
  planFormCtaButtonTextFont: createStylesParam('plan-form-cta-button-text-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-M',
    //   },
    // },
  }),
  planFormCtaButtonBackgroundColor: createStylesParam('plan-form-cta-button-background-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-fill-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Background color for the primary call-to-action button (e.g., Submit, Continue) on the plan customization form. You can use this to change the background color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-fill-primary',
      },
    },
  }),
  planFormCtaButtonBackgroundColorHover: createStylesParam('plan-form-cta-button-background-color-hover', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-fill-primary-hover'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Background color for the primary call-to-action button (e.g., Submit, Continue) on the plan customization form when hovered. You can use this to change the background color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-fill-primary-hover',
      },
    },
  }),
  planFormCtaButtonBorderColor: createStylesParam('plan-form-cta-button-border-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-border-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Border color for the primary call-to-action button (e.g., Submit, Continue) on the plan customization form. You can use this to change the border color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-border-primary',
      },
    },
  }),
  planFormCtaButtonBorderColorHover: createStylesParam('plan-form-cta-button-border-color-hover', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-border-primary-hover'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Border color for the primary call-to-action button (e.g., Submit, Continue) on the plan customization form when hovered. You can use this to change the border color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-border-primary-hover',
      },
    },
  }),
  planFormCtaButtonBorderRadius: createStylesParam('plan-form-cta-button-border-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      ai: {
        description:
          'Border radius of the primary call-to-action button (e.g., Submit, Continue) on the plan customization form in pixels. You can use this to change the border radius.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planFormCtaButtonBorderWidth: createStylesParam('plan-form-cta-button-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      ai: {
        description:
          'Border width of the primary call-to-action button (e.g., Submit, Continue) on the plan customization form in pixels. You can use this to change the border width.',
      },
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  }),
  planFormButtonsTextColor: createStylesParam('plan-form-buttons-text-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-text-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color for other buttons within form fields on the plan customization form. You can use this to change the text color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-text-primary',
      },
    },
  }),
  planFormButtonsTextColorHover: createStylesParam('plan-form-buttons-text-color-hover', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-text-primary-hover'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Text color for other buttons within form fields on the plan customization form when hovered. You can use this to change the text color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-text-primary-hover',
      },
    },
  }),
  planFormButtonsTextFont: createStylesParam('plan-form-buttons-text-font', {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    // metadata: {
    //   type: StyleType.font,
    //   defaultValue: {
    //     type: 'static',
    //     preset: 'Body-M',
    //   },
    // },
  }),
  planFormButtonsBackgroundColor: createStylesParam('plan-form-buttons-background-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-fill-primary'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Background color for other buttons within form fields on the plan customization form. You can use this to change the background color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-fill-primary',
      },
    },
  }),
  planFormButtonsBackgroundColorHover: createStylesParam('plan-form-buttons-background-color-hover', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-fill-primary-hover'),
    metadata: {
      type: StyleType.color,
      ai: {
        description:
          'Background color for other buttons within form fields on the plan customization form when hovered. You can use this to change the background color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-fill-primary-hover',
      },
    },
  }),
  planFormButtonsBorderColor: createStylesParam('plan-form-buttons-border-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-border-primary'),
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description:
          'Border color for other buttons within form fields on the plan customization form. You can use this to change the border color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-border-primary',
      },
    },
  }),
  planFormButtonsBorderColorHover: createStylesParam('plan-form-buttons-border-color-hover', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('button-color-border-primary-hover'),
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description:
          'Border color for other buttons within form fields on the plan customization form when hovered. You can use this to change the border color.',
      },
      defaultValue: {
        type: 'static',
        preset: 'button-color-border-primary-hover',
      },
    },
  }),
  planFormButtonsBorderRadius: createStylesParam('plan-form-buttons-border-radius', {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      ai: {
        description:
          'Border radius of other buttons within form fields on the plan customization form in pixels. You can use this to change the border radius.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }),
  planFormButtonsBorderWidth: createStylesParam('plan-form-buttons-border-width', {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      ai: {
        description:
          'Border width of other buttons within form fields on the plan customization form in pixels. You can use this to change the border width.',
      },
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  }),
});

export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const planCardShadowAngle = styleParams.numbers[componentStyles.planCardShadowAngle.key!] ?? 0;
  const planCardShadowDistance = styleParams.numbers[componentStyles.planCardShadowDistance.key!] ?? 0;

  const planFormShadowAngle = styleParams.numbers[componentStyles.planFormShadowAngle.key!] ?? 0;
  const planFormShadowDistance = styleParams.numbers[componentStyles.planFormShadowDistance.key!] ?? 0;

  const formInputBorderWidths = getFormInputBorderWidths({ styleParams });

  const planCardAlignment = styleParams.numbers[componentStyles.planCardAlignment.key!] ?? Alignment.CENTER;
  return {
    'plan-card-text-align': alignmentToTextAlignValue(planCardAlignment),
    'plan-card-align-items': alignmentToAlignItemsValue(planCardAlignment),

    'plan-card-shadow-offset-x': -Math.round(Math.sin((planCardShadowAngle * Math.PI) / 180) * planCardShadowDistance),
    'plan-card-shadow-offset-y': Math.round(Math.cos((planCardShadowAngle * Math.PI) / 180) * planCardShadowDistance),

    'plan-form-shadow-offset-x': -Math.round(Math.sin((planFormShadowAngle * Math.PI) / 180) * planFormShadowDistance),
    'plan-form-shadow-offset-y': Math.round(Math.cos((planFormShadowAngle * Math.PI) / 180) * planFormShadowDistance),

    ...formInputBorderWidths,
  };
};

export default componentStyles;

function alignmentToTextAlignValue(alignment: Alignment): 'left' | 'center' | 'right' {
  switch (alignment) {
    case Alignment.LEFT:
      return 'left';
    case Alignment.CENTER:
      return 'center';
    case Alignment.RIGHT:
      return 'right';
  }
}

function alignmentToAlignItemsValue(alignment: Alignment): 'start' | 'center' | 'end' {
  switch (alignment) {
    case Alignment.LEFT:
      return 'start';
    case Alignment.CENTER:
      return 'center';
    case Alignment.RIGHT:
      return 'end';
  }
}

function getFormInputBorderWidths({ styleParams }: { styleParams: Parameters<CustomCssVarsFn>[0]['styleParams'] }) {
  const borderWidth = styleParams.numbers[componentStyles.planFormFieldBorderWidth.key!];
  const borderStyle = styleParams.numbers[componentStyles.planFormFieldBorderStyle.key!];

  const defaultBorders = {
    'plan-form-field-border-left-width': borderWidth,
    'plan-form-field-border-right-width': borderWidth,
    'plan-form-field-border-top-width': borderWidth,
    'plan-form-field-border-bottom-width': borderWidth,
  };

  const borders = {
    [FormFieldBorderStyle.None]: {
      'plan-form-field-border-left-width': 0,
      'plan-form-field-border-right-width': 0,
      'plan-form-field-border-top-width': 0,
      'plan-form-field-border-bottom-width': 0,
    },
    [FormFieldBorderStyle.Bottom]: {
      'plan-form-field-border-left-width': 0,
      'plan-form-field-border-right-width': 0,
      'plan-form-field-border-top-width': 0,
      'plan-form-field-border-bottom-width': borderWidth,
    },
    [FormFieldBorderStyle.All]: defaultBorders,
  }[borderStyle];

  return borders || defaultBorders;
}
